import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Animated, FlatListProps, Pressable, View } from 'react-native'
import { useModalize } from 'react-native-modalize'

import { PINNED_TOKENS } from '@ambire-common/consts/pinnedTokens'
import { Network } from '@ambire-common/interfaces/network'
import { AssetType } from '@ambire-common/libs/defiPositions/types'
import { getTokenAmount, getTokenBalanceInUSD } from '@ambire-common/libs/portfolio/helpers'
import { TokenResult } from '@ambire-common/libs/portfolio/interfaces'
import formatDecimals from '@ambire-common/utils/formatDecimals/formatDecimals'
import DownArrowIcon from '@common/assets/svg/DownArrowIcon'
import RightArrowIcon from '@common/assets/svg/RightArrowIcon'
import Button from '@common/components/Button'
import Text from '@common/components/Text'
import { useTranslation } from '@common/config/localization'
import useNavigation from '@common/hooks/useNavigation'
import useTheme from '@common/hooks/useTheme'
import { WEB_ROUTES } from '@common/modules/router/constants/common'
import spacings from '@common/styles/spacings'
import flexbox from '@common/styles/utils/flexbox'
import { tokenOrCollectionSearch } from '@common/utils/search'
import useAppMode from '@web/hooks/useAppMode'
import useNetworksControllerState from '@web/hooks/useNetworksControllerState'
import usePortfolioControllerState from '@web/hooks/usePortfolioControllerState/usePortfolioControllerState'
import useRailgunContext from '@web/hooks/useRailgunContext'
import useRailgunTokens from '@web/hooks/useRailgunTokens/useRailgunTokens'
import useSelectedAccountControllerState from '@web/hooks/useSelectedAccountControllerState'
import AddTokenBottomSheet from '@web/modules/settings/screens/ManageTokensSettingsScreen/AddTokenBottomSheet'
import { getTokenId } from '@web/utils/token'
import { getUiType } from '@web/utils/uiType'

import { ModeEnum } from '@web/contexts/appModeContext'

import { getNetworkIdsWithPriceError, splitDustTokens } from '../../helpers/dustTokens'
import DashboardBanners from '../DashboardBanners'
import DashboardPageScrollContainer from '../DashboardPageScrollContainer'
import TabsAndSearch from '../TabsAndSearch'
import { TabType } from '../TabsAndSearch/Tabs/Tab/Tab'
import TokenItem from './TokenItem'
import Skeleton from './TokensSkeleton'

interface Props {
  openTab: TabType
  setOpenTab: React.Dispatch<React.SetStateAction<TabType>>
  sessionId: string
  initTab?: {
    [key: string]: boolean
  }
  onScroll: FlatListProps<any>['onScroll']
  dashboardNetworkFilterName: string | null
  animatedOverviewHeight: Animated.Value
}

// if any of the post amount (during simulation) or the current state
// has a balance above 0, we should consider it legit and show it
const hasAmount = (token: TokenResult) => {
  return token.amount > 0n || (token.amountPostSimulation && token.amountPostSimulation > 0n)
}
// if the token is on the gas tank and the network is not a relayer network (a custom network)
// we should not show it on dashboard
const isGasTankTokenOnCustomNetwork = (token: TokenResult, networks: Network[]) => {
  return token.flags.onGasTank && !networks.find((n) => n.chainId === token.chainId && n.hasRelayer)
}

const { isPopup } = getUiType()

const Tokens = ({
  openTab,
  setOpenTab,
  initTab,
  sessionId,
  onScroll,
  animatedOverviewHeight,
  dashboardNetworkFilterName
}: Props) => {
  const { t } = useTranslation()
  const { navigate } = useNavigation()
  const { theme } = useTheme()
  const { mode } = useAppMode()
  const { networks } = useNetworksControllerState()
  const { customTokens } = usePortfolioControllerState()
  const { portfolio, dashboardNetworkFilter, balanceAffectingErrors } =
    useSelectedAccountControllerState()
  const { isBalanceFetching: isRailgunBalanceFetching, currentRailgunWallet } = useRailgunContext()
  const railgunTokens = useRailgunTokens()
  const {
    ref: addTokenBottomSheetRef,
    open: openAddTokenBottomSheet,
    close: closeAddTokenBottomSheet
  } = useModalize()
  const { control, watch, setValue } = useForm({
    mode: 'all',
    defaultValues: {
      search: ''
    }
  })

  const searchValue = watch('search')

  const isPrivateMode = mode === ModeEnum.PRIVATE

  const tokens = useMemo(() => {
    if (isPrivateMode) {
      const filteredByNetwork = railgunTokens.filter((token) => {
        if (!dashboardNetworkFilter) return true
        if (dashboardNetworkFilter === 'rewards' || dashboardNetworkFilter === 'gasTank')
          return false
        return token?.chainId?.toString() === dashboardNetworkFilter.toString()
      })

      if (!searchValue) return filteredByNetwork

      return tokenOrCollectionSearch({
        networks,
        assets: filteredByNetwork,
        search: searchValue,
        searchType: 'token'
      }) as TokenResult[]
    }

    const filteredTokens = (portfolio?.tokens || [])
      .filter((token) => !token.flags.onGasTank && token.flags.defiTokenType !== AssetType.Borrow)
      .filter((token) => {
        if (!dashboardNetworkFilter) return true
        if (dashboardNetworkFilter === 'rewards') return token.flags.rewardsType
        if (dashboardNetworkFilter === 'gasTank') return token.flags.onGasTank

        return (
          token?.chainId?.toString() === dashboardNetworkFilter.toString() && !token.flags.onGasTank
        )
      })

    if (!searchValue) return filteredTokens

    return tokenOrCollectionSearch({
      networks,
      assets: filteredTokens,
      search: searchValue,
      searchType: 'token'
    }) as TokenResult[]
  }, [
    isPrivateMode,
    railgunTokens,
    portfolio?.tokens,
    dashboardNetworkFilter,
    searchValue,
    networks
  ])

  const userHasNoBalance = useMemo(
    // Exclude gas tank tokens from the check
    // as new users get some Gas Tank balance by default
    () => !tokens.some((token) => !token.flags.onGasTank && hasAmount(token)),
    [tokens]
  )

  const sortedTokens = useMemo(() => {
    if (isPrivateMode) {
      return [...tokens].sort((a, b) => {
        const aBalance = getTokenBalanceInUSD(a)
        const bBalance = getTokenBalanceInUSD(b)
        if (aBalance === bBalance) {
          return Number(getTokenAmount(b)) - Number(getTokenAmount(a))
        }
        return bBalance - aBalance
      })
    }
    return tokens
      .filter((token) => {
        if (isGasTankTokenOnCustomNetwork(token, networks)) return false
        if (token?.flags.isHidden) return false

        const hasTokenAmount = hasAmount(token)
        const isCustom = customTokens.find(
          ({ address, chainId }) =>
            token.address.toLowerCase() === address.toLowerCase() &&
            token.chainId === chainId &&
            !token.flags.rewardsType // exclude rewards from custom tokens
        )
        const isPinned = PINNED_TOKENS.find(
          ({ address, chainId }) =>
            token.address.toLowerCase() === address.toLowerCase() &&
            token.chainId === chainId &&
            // exclude projected rewards from pinned tokens
            token.flags.rewardsType !== 'wallet-projected-rewards'
        )

        return (
          hasTokenAmount ||
          isCustom ||
          // Don't display pinned tokens until we are sure the user has no balance
          (isPinned && userHasNoBalance && portfolio?.isAllReady)
        )
      })
      .sort((a, b) => {
        // pending tokens go on top
        if (
          typeof a.amountPostSimulation === 'bigint' &&
          a.amountPostSimulation !== BigInt(a.amount)
        ) {
          return -1
        }
        if (
          typeof b.amountPostSimulation === 'bigint' &&
          b.amountPostSimulation !== BigInt(b.amount)
        ) {
          return 1
        }

        // If a is a rewards token and b is not, a should come before b.
        if (a.flags.rewardsType && !b.flags.rewardsType) {
          return -1
        }
        if (!a.flags.rewardsType && b.flags.rewardsType) {
          // If b is a rewards token and a is not, b should come before a.
          return 1
        }

        const aBalance = getTokenBalanceInUSD(a)
        const bBalance = getTokenBalanceInUSD(b)

        if (a.flags.rewardsType === b.flags.rewardsType) {
          if (aBalance === bBalance) {
            return Number(getTokenAmount(b)) - Number(getTokenAmount(a))
          }

          return bBalance - aBalance
        }

        if (a.flags.onGasTank && !b.flags.onGasTank) {
          return -1
        }
        if (!a.flags.onGasTank && b.flags.onGasTank) {
          return 1
        }

        return 0
      })
  }, [tokens, isPrivateMode, networks, customTokens, userHasNoBalance, portfolio?.isAllReady])

  // Dust/spam grouping (public mode only): tokens with a balance but no real
  // USD price — or worth less than a cent — collapse behind an expandable
  // "N other tokens" row (scam airdrops surface via portfolio hints after any
  // on-chain activity). Searching bypasses the grouping so they stay findable.
  const [isDustExpanded, setIsDustExpanded] = useState(false)

  const networkIdsWithPriceError = useMemo(
    () => getNetworkIdsWithPriceError(balanceAffectingErrors, networks),
    [balanceAffectingErrors, networks]
  )

  const { visibleTokens, dustTokens } = useMemo(() => {
    if (isPrivateMode || searchValue) {
      return { visibleTokens: sortedTokens, dustTokens: [] as TokenResult[] }
    }
    return splitDustTokens(sortedTokens, networkIdsWithPriceError, (token) =>
      // The controller list is authoritative for user-added tokens; don't rely
      // solely on token.flags.isCustom (mirrors the isCustom check above).
      customTokens.some(
        ({ address, chainId }) =>
          token.address.toLowerCase() === address.toLowerCase() && token.chainId === chainId
      )
    )
  }, [isPrivateMode, searchValue, sortedTokens, networkIdsWithPriceError, customTokens])

  const dustTotalUSD = useMemo(
    () => dustTokens.reduce((sum, token) => sum + getTokenBalanceInUSD(token), 0),
    [dustTokens]
  )

  const hiddenTokensCount = useMemo(
    () => tokens.filter((token) => token.flags.isHidden).length,
    [tokens]
  )

  // In private mode, suppress the "no tokens" empty state while a cached aggregate balance
  // (totalBalanceUSD) exists but the per-token map hasn't been rehydrated yet — otherwise the
  // overview shows a cached USD total while the tokens tab claims the wallet is empty.
  const hasCachedRailgunBalance =
    isPrivateMode && Number(currentRailgunWallet?.totalBalanceUSD ?? 0) > 0
  const isTokensReady = isPrivateMode
    ? !isRailgunBalanceFetching && !(hasCachedRailgunBalance && sortedTokens.length === 0)
    : Boolean(portfolio?.isAllReady)

  const navigateToAddCustomToken = useCallback(() => {
    openAddTokenBottomSheet()
  }, [openAddTokenBottomSheet])

  const listData = useMemo(() => {
    const hasAnyRows = visibleTokens.length > 0 || dustTokens.length > 0
    const data: any[] = ['header']

    data.push(!hasAnyRows && !isTokensReady ? 'skeleton' : 'keep-this-to-avoid-key-warning')

    if (initTab?.tokens) {
      data.push(...visibleTokens)

      if (dustTokens.length > 0) {
        if (isDustExpanded) data.push(...dustTokens, 'dust-collapse')
        else data.push('dust-summary')
      }
    }

    data.push(hasAnyRows && !isTokensReady ? 'skeleton' : 'keep-this-to-avoid-key-warning-2')
    data.push(!hasAnyRows && isTokensReady ? 'empty' : '')
    data.push('footer')

    return data
  }, [visibleTokens, dustTokens, isDustExpanded, isTokensReady, initTab?.tokens])

  const renderItem = useCallback(
    ({ item, index }: any) => {
      if (item === 'header') {
        return (
          <View
            style={{
              // Opaque card surface so scrolled token rows are fully occluded behind
              // the sticky header (tab row + Total assets / Amount-USD column header)
              // instead of bleeding through it. Horizontal inset comes from the card
              // shell's own padding (DashboardPages), so the header adds none here —
              // it stays aligned with the rows below it.
              backgroundColor: theme.homeCardBackground as string,
              paddingTop: 0
            }}
          >
            <TabsAndSearch
              openTab={openTab}
              setOpenTab={setOpenTab}
              currentTab="tokens"
              searchControl={control}
              sessionId={sessionId}
            />
            <View
              style={[
                flexbox.directionRow,
                flexbox.justifySpaceBetween,
                spacings.mbTy,
                spacings.phTy
              ]}
            >
              <Text appearance="secondaryText" fontSize={12} weight="medium">
                {t('Total assets')}
              </Text>
              <Text appearance="secondaryText" fontSize={12} weight="medium">
                {t('Amount/USD')}
              </Text>
            </View>
          </View>
        )
      }

      if (item === 'empty') {
        return (
          <View style={[flexbox.alignCenter, spacings.pv]}>
            <Text testID="no-tokens-text" fontSize={16} weight="medium">
              {!searchValue && !dashboardNetworkFilterName && t("You don't have any tokens yet.")}
              {!searchValue &&
                dashboardNetworkFilterName &&
                t(`No tokens found on ${dashboardNetworkFilterName}.`)}
              {searchValue &&
                t(
                  `No tokens match "${searchValue}"${
                    dashboardNetworkFilterName ? ` on ${dashboardNetworkFilterName}` : ''
                  }.`
                )}
            </Text>
          </View>
        )
      }

      if (item === 'skeleton')
        return (
          <View style={spacings.ptTy}>
            {/* Display more skeleton items if there are no tokens */}
            <Skeleton amount={3} withHeader={false} />
          </View>
        )

      if (item === 'dust-summary' || item === 'dust-collapse') {
        const isSummary = item === 'dust-summary'
        return (
          <Pressable
            testID="other-tokens-toggle"
            style={[
              flexbox.directionRow,
              flexbox.alignCenter,
              flexbox.justifySpaceBetween,
              spacings.pvTy,
              spacings.phTy,
              spacings.mhTy,
              spacings.mvMi,
              {
                borderRadius: 4,
                backgroundColor: theme.secondaryBackground
              }
            ]}
            onPress={() => setIsDustExpanded(isSummary)}
          >
            <View style={[flexbox.directionRow, flexbox.alignCenter]}>
              <View
                style={[
                  spacings.mrTy,
                  { transform: [{ rotate: isSummary ? '-90deg' : '180deg' }] }
                ]}
              >
                <DownArrowIcon height={12} width={12} color={theme.secondaryText} />
              </View>
              <Text appearance="secondaryText" fontSize={12} weight="medium">
                {isSummary
                  ? t('{{count}} other {{tokensLabel}}', {
                      count: dustTokens.length,
                      tokensLabel: dustTokens.length > 1 ? t('tokens') : t('token')
                    })
                  : t('Hide {{count}} other {{tokensLabel}}', {
                      count: dustTokens.length,
                      tokensLabel: dustTokens.length > 1 ? t('tokens') : t('token')
                    })}
              </Text>
            </View>
            {isSummary && (
              <Text appearance="secondaryText" fontSize={12} weight="medium">
                {formatDecimals(dustTotalUSD, 'value')}
              </Text>
            )}
          </Pressable>
        )
      }

      if (item === 'footer') {
        return isTokensReady &&
          // A trick to render the button once all tokens have been rendered. Otherwise
          // there will be layout shifts
          index === listData.length - 1 ? (
          <View style={hiddenTokensCount ? spacings.ptTy : spacings.ptSm}>
            {!!hiddenTokensCount && (
              <Pressable
                style={[
                  flexbox.directionRow,
                  flexbox.alignCenter,
                  flexbox.justifySpaceBetween,
                  spacings.pvMi,
                  spacings.phTy,
                  spacings.mhTy,
                  spacings.mbLg,
                  {
                    borderRadius: 4,
                    backgroundColor: theme.secondaryBackground
                  }
                ]}
                onPress={() => {
                  navigate(WEB_ROUTES.manageTokens)
                }}
              >
                <Text appearance="secondaryText" fontSize={12}>
                  {t('You have {{count}} hidden {{tokensLabel}}', {
                    count: hiddenTokensCount,
                    tokensLabel: hiddenTokensCount > 1 ? t('tokens') : t('token')
                  })}{' '}
                  {dashboardNetworkFilter ? t('on this network') : null}
                </Text>
                <RightArrowIcon height={12} color={theme.secondaryText} />
              </Pressable>
            )}
            {!isPrivateMode && (
              <Button
                type="secondary"
                text={t('+ Add custom token')}
                onPress={navigateToAddCustomToken}
              />
            )}
          </View>
        ) : null
      }

      if (
        !initTab?.tokens ||
        !item ||
        item === 'keep-this-to-avoid-key-warning' ||
        item === 'keep-this-to-avoid-key-warning-2'
      )
        return null

      return (
        <TokenItem
          token={item}
          networks={networks}
          simulatedAccountOp={portfolio.networkSimulatedAccountOp[item.chainId.toString()]}
        />
      )
    },
    [
      initTab?.tokens,
      networks,
      portfolio.networkSimulatedAccountOp,
      theme.primaryBackground,
      theme.secondaryBackground,
      theme.secondaryText,
      theme.homeCardBackground,
      openTab,
      setOpenTab,
      control,
      sessionId,
      t,
      searchValue,
      dashboardNetworkFilterName,
      isTokensReady,
      listData.length,
      dustTokens.length,
      dustTotalUSD,
      hiddenTokensCount,
      dashboardNetworkFilter,
      navigateToAddCustomToken,
      navigate,
      isPrivateMode
    ]
  )

  const keyExtractor = useCallback((tokenOrElement: any) => {
    if (typeof tokenOrElement === 'string') {
      return tokenOrElement
    }

    return getTokenId(tokenOrElement)
  }, [])

  useEffect(() => {
    setValue('search', '')
  }, [setValue])

  return (
    <>
      {/* The home-v2 rounded card shell is owned by DashboardPages (one shared shell
          wraps whichever tab is active). Tokens just renders its scroll list inside it. */}
      <DashboardPageScrollContainer
        tab="tokens"
        openTab={openTab}
        ListHeaderComponent={<DashboardBanners />}
        animatedOverviewHeight={animatedOverviewHeight}
        data={listData}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        onEndReachedThreshold={isPopup ? 5 : 2.5}
        initialNumToRender={isPopup ? 10 : 20}
        windowSize={9} // Larger values can cause performance issues.
        onScroll={onScroll}
      />
      <AddTokenBottomSheet
        sheetRef={addTokenBottomSheetRef}
        handleClose={closeAddTokenBottomSheet}
      />
    </>
  )
}

export default React.memo(Tokens)
